import { Setting, SettingDetail, SettingInfo, SettingsSection, SettingsSectionHeading } from 'components/Setting/style';
import { Radio } from 'components/Radio';
import { RadioForm, RadioGroup } from 'components/Radio/style';
import { CustomAnchoredToolTip } from 'components/Table';
import { ParagraphXSmallRegular } from 'components/Typography';
import { STRINGS } from 'config';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getClosingTimeBehaviour, saveClosingTimeBehaviour } from 'store/action/closingTimeBehaviour';
import { useAppState } from 'store/configureStore';
import _debounce from 'lodash/debounce';
import { SaveButton } from 'components/AdminAvailability/style';

const { REQUEST_STATUS: { IDLE, PENDING, SUCCEEDED, FAILED } } = STRINGS;

const CLOSING_TIME_BEHAVIOUR = {
  ALLOW_COMPLETION: {
    id: 'ALLOW_COMPLETION',
    label: 'Allow completion',
  },
  PREVENT_SUBMISSION: {
    id: 'PREVENT_SUBMISSION',
    label: 'Prevent submission',
  },
};

export const ClosingTimeBehaviour = () => {
  const {
    app: {
      selectedPractice,
      selectedPracticeIsEhub,
      selectedPracticeHasAdminRights,
    },
  } = useAppState();

  const [closingTimeBehaviour, setClosingTimeBehaviour] = useState(null);
  const [getRequestStatus, setGetRequestStatus] = useState(IDLE);
  const [saveRequestStatus, setSaveRequestStatus] = useState(IDLE);

  const isGetRequestPending = getRequestStatus === PENDING;
  const isSaveRequestPending = saveRequestStatus === PENDING;
  const isSaveDisabled = !selectedPracticeHasAdminRights
    || selectedPracticeIsEhub
    || isSaveRequestPending;
  const isValueValidForFeature = Object.keys(CLOSING_TIME_BEHAVIOUR).includes(closingTimeBehaviour?.id);

  const onChangeClosingTimeBehaviour = event => {
    setClosingTimeBehaviour(CLOSING_TIME_BEHAVIOUR[event.target.value]);
  };

  const onSubmit = useCallback(() => {
    if (!selectedPractice || !selectedPracticeHasAdminRights || isSaveRequestPending || isGetRequestPending) {
      return null;
    }

    setSaveRequestStatus(PENDING);

    return saveClosingTimeBehaviour(selectedPractice?.id, closingTimeBehaviour.id).then(isSuccess => {
      if (!isSuccess) {
        return setSaveRequestStatus(FAILED);
      }

      return setSaveRequestStatus(SUCCEEDED);
    });
  }, [
    closingTimeBehaviour,
    isGetRequestPending,
    isSaveRequestPending,
    selectedPractice,
    selectedPracticeHasAdminRights,
  ]);

  const debouncedSubmit = useMemo(() => _debounce(onSubmit, 2000, { leading: true }), [onSubmit]);

  useEffect(() => {
    setGetRequestStatus(PENDING);
    getClosingTimeBehaviour(selectedPractice?.id).then(response => {
      setGetRequestStatus(Object.keys(CLOSING_TIME_BEHAVIOUR).includes(response.id) ? SUCCEEDED : FAILED);
      setSaveRequestStatus(IDLE);
      setClosingTimeBehaviour(CLOSING_TIME_BEHAVIOUR[response] || CLOSING_TIME_BEHAVIOUR.ALLOW_COMPLETION);
    });
  }, [selectedPractice?.id]);

  return (
    <SettingsSection data-automation="ClosingTimeBehaviourWrapper">
      <Setting>
        <SettingInfo>
          <SettingsSectionHeading data-automation="ClosingTimeBehaviourHeading">
            Closing time behaviour
          </SettingsSectionHeading>

          <ParagraphXSmallRegular>
            Control whether patients can submit a consultation they started before closing time.
          </ParagraphXSmallRegular>
        </SettingInfo>

        <SettingDetail>
          <RadioForm
            action=""
            onSubmit={event => {
              event.preventDefault();
              debouncedSubmit();
            }}
            autocomplete="off"
            data-automation="ClosingTimeBehaviourForm"
          >
            <RadioGroup>
              {Object.values(CLOSING_TIME_BEHAVIOUR).map(({ id, label }) => (
                <Radio
                  key={`closing-time-behaviour-option--${id}`}
                  id={id}
                  fieldName="closingTimeBehaviour"
                  value={id}
                  isChecked={id === closingTimeBehaviour?.id}
                  defaultChecked={id === closingTimeBehaviour?.id && isValueValidForFeature}
                  onChange={onChangeClosingTimeBehaviour}
                  label={label}
                />
              ))}
            </RadioGroup>

            <SaveButton
              size="small"
              onClick={onSubmit}
              disabled={isSaveDisabled}
              type="submit"
              data-automation="SaveClosingTimeBehaviourButton"
            >
              Save changes
              {!selectedPracticeHasAdminRights && (
                <CustomAnchoredToolTip automationLabel="SaveClosingTimeBehaviourDisabledTooltip" label="Contact your practice admin" align="top" />
              )}
            </SaveButton>
          </RadioForm>
        </SettingDetail>
      </Setting>
    </SettingsSection>
  );
};
